import {
  BodyText,
  Button,
  Field,
  FieldError,
  FieldLabel,
  HeaderText,
  Textarea,
  toast,
} from "@/components/ui";
import { Card, CardContent } from "@/components/ui/Card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { Tooltip } from "@/components/ui/Tooltip";
import { useGetPatient } from "@/features/Patient/network/usePatient";
import { useRoomToken } from "@/features/Patient/network/useRoomToken";
import { useSendNoriOutreach } from "@/features/Patient/network/useSendNoriOutreach";
import { isUniversalClinicPatient } from "@/features/Patient/utils";
import {
  DistributionAction,
  useAvailableActions,
} from "@/network/useAvailableActions";
import { zodResolver } from "@hookform/resolvers/zod";
import { useParams, useSearch } from "@tanstack/react-router";
import { MessageSquareShare, SendHorizontal, Sparkle } from "lucide-react";
import { useState } from "react";
import { Controller, useForm, useFormState } from "react-hook-form";
import { z } from "zod";

const OutreachSchema = z.object({
  message: z.string().min(1, "Message is required"),
});

type OutreachFormValues = z.infer<typeof OutreachSchema>;

export const NoriOutreachButton = () => {
  const [open, setOpen] = useState(false);
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const { encounterId } = useSearch({ from: "/patient/$patientId" });
  const { data: patient } = useGetPatient(patientId as string);
  const { data: roomToken } = useRoomToken({
    patientId: patientId as string,
    encounterId: encounterId?.toString(),
  });

  const { data: actions } = useAvailableActions({
    patientId,
    encounterId: encounterId as string | undefined,
  });

  const form = useForm<OutreachFormValues>({
    resolver: zodResolver(OutreachSchema),
    defaultValues: {
      message: "",
    },
  });

  const { isValid } = useFormState({ control: form.control });

  const { mutate: sendOutreach, isPending } = useSendNoriOutreach({
    roomToken,
    onSuccess: () => {
      toast.success("Outreach message sent successfully");
      setOpen(false);
      form.reset();
    },
    onError: (error) => {
      toast.error(`Failed to send outreach: ${error.message}`);
    },
  });

  // Only show when we have an encounter ID
  if (!encounterId) {
    return null;
  }

  // Only show for Universal Clinic patients
  if (!isUniversalClinicPatient(patient)) {
    return null;
  }

  const noriOutreachAction = actions?.[DistributionAction.START_NORI_OUTREACH];
  const isAllowed = noriOutreachAction?.allowed ?? false;
  const notAllowedReason =
    noriOutreachAction?.notAllowedDebugMessage ||
    noriOutreachAction?.notAllowedReason;

  // Hide button if no reason is provided
  if (!isAllowed && !notAllowedReason) {
    return null;
  }

  const onSubmit = (values: OutreachFormValues) => {
    sendOutreach({ message: values.message });
  };

  const button = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogOverlay />
      <DialogTrigger asChild>
        <Button
          variant="primary"
          size="xs"
          disabled={!isAllowed}
          leftIcon={MessageSquareShare}
          data-testid="nori-outreach-button"
        >
          Outreach
        </Button>
      </DialogTrigger>
      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        data-testid="nori-outreach-dialog-modal"
        className="max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>Outreach Message</DialogTitle>
        </DialogHeader>
        <Card className="bg-superclinical w-full">
          <CardContent className="p-2 gap-2 flex flex-col">
            <div className="flex gap-1">
              <Sparkle className="w-4 h-4 text-superclinical" />
              <HeaderText className="text-superclinical" size="sm">
                CHAI Tips
              </HeaderText>
            </div>
            <ul className="list-disc list-inside">
              <li>
                <BodyText size="sm">
                  Patients receive outreach messages as{" "}
                  <b> new CHAI sessions</b>
                </BodyText>
              </li>
              <li>
                <BodyText size="sm">
                  They <b>won't</b> be able to directly reply back to you
                </BodyText>
              </li>
              <li>
                <BodyText size="sm">
                  If needed, they can start a <b>new visit</b> in response to a
                  message
                </BodyText>
              </li>
            </ul>
          </CardContent>
        </Card>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <Controller
            control={form.control}
            name="message"
            render={({ field, fieldState }) => (
              <Field data-invalid={fieldState.invalid}>
                <FieldLabel htmlFor="message">Message</FieldLabel>
                <Textarea
                  {...field}
                  id="message"
                  placeholder="Enter your outreach message..."
                  className="min-h-[168px] max-h-[40vh] overflow-y-auto"
                  aria-invalid={fieldState.invalid}
                />
                <FieldError errors={[fieldState.error]} />
              </Field>
            )}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isPending || !isValid}
            >
              <span className="flex gap-1">
                <SendHorizontal className="w-4 h-4" />
                Send
              </span>
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );

  // If not allowed, wrap in tooltip with reason
  if (!isAllowed && notAllowedReason) {
    return (
      <Tooltip tooltipContentText={notAllowedReason} side="bottom">
        {button}
      </Tooltip>
    );
  }

  return button;
};
